import {
  FormHelperText as MuiFormHelperText,
  FormHelperTextProps as MuiFormHelperTextProps,
  useFormControl,
} from '@mui/material';
import { AlertCircleInvert } from '@portals/icons';
import i18next from 'i18next';
import React, { isValidElement } from 'react';

import { Typography } from '../Typography/Typography';

// eslint-disable-next-line import/no-named-as-default-member
const t = i18next.getFixedT.bind(i18next)(null, 'core', 'text');

type FormHelperTextProps = MuiFormHelperTextProps & {
  /**
   * Max Char Count Size (for display purpose, not for validation)
   */
  maxLength?: HTMLInputElement['maxLength'];

  /**
   * Min Char Count Size (for display purpose, not for validation)
   */
  minLength?: HTMLInputElement['minLength'];

  /**
   * Length of Value (for display purpose, not for validation)
   */
  length?: number;

  /**
   * Show a Text Counter (using length)
   */
  showCurrentLength?: boolean;

  /**
   * Show a Text Counter (using maxLength)
   */
  showMaxLength?: boolean;

  /**
   * Show a Text Counter (using minLength)
   */
  showMinLength?: boolean;
};

const STYLE = { display: 'flex', justifyContent: 'flex-start' };

function isEmptyChildren(node: unknown) {
  if (!node || node === ' ') {
    return true;
  }

  if (Array.isArray(node) && node.length === 0) {
    return true;
  }

  if (
    isValidElement<{ children: string | unknown }>(node) &&
    (node.props.children === '​' || node.props.children === '')
  ) {
    return true;
  }

  return !node;
}

export const FormHelperText: React.FC<FormHelperTextProps> = ({
  children,
  maxLength,
  minLength,
  showMinLength,
  showMaxLength,
  showCurrentLength,
  length,
  ...props
}) => {
  const state = useFormControl();
  const error = typeof props.error === 'boolean' ? props.error : state?.error;

  return (
    <MuiFormHelperText error={error} component="div" sx={STYLE} {...props}>
      {error && !isEmptyChildren(children) ? <AlertCircleInvert /> : null}
      <Typography variant="h6" component="div" color={error ? 'error.main' : 'grey.main'}>
        {children}
      </Typography>
      {showMaxLength || showCurrentLength || showMinLength ? (
        <Typography
          variant="h6"
          component="div"
          color={error && showMaxLength && maxLength ? 'error.main' : 'grey.main'}
          marginLeft="auto"
        >
          {showMaxLength && maxLength ? t('charsMax', { length, maxLength }) : null}
          {showMinLength && minLength ? t('charsMax', { length, maxLength }) : null}
          {!showMaxLength && !showMinLength ? t('chars', { length }) : null}
        </Typography>
      ) : null}
    </MuiFormHelperText>
  );
};
